import { COMMANDS, commandKey, isSystemCommand, newId, type CommandKey, type PayloadOf } from '@gk/contracts'
import { getMeta, intentKeyOf, localOnlyTables, MIRROR_STORES, setMeta, type GkDb, type OutboxRow } from './db.ts'
import { PROJECTORS } from './projectors.ts'
import { keyedPayload } from './transient.ts'

export interface EnqueueContext {
  orgId: string
  siteId: string
  personId: string
  deviceId: string
  appVersion: string
  authMode?: 'personal' | 'kiosk'
  /** On a shared workstation: the open session (api.kiosk_unlock) of the person who acts. */
  kioskUnlockId?: string
}
export interface EnqueueOptions { /** written INSIDE the same local transaction as the outbox row (a photo's bytes next to the command that announces them) */ alsoWrite?: (row: OutboxRow) => Promise<void>; aggregateId?: string; dependsOn?: string[]; intentKey?: string; occurredAt?: Date; /** single-use server nonce of an online-only command (api.begin_online_action) */ nonce?: string; /** kept on the row, never sent (OutboxRow.local_tag) */ localTag?: string }

const LIVE = new Set(['queued', 'sending', 'transient', 'blocked'])

/** Record a user's confirmed action as a command. The outbox entry and its local projection are written in ONE Dexie
 *  transaction (ARCHITECTURE 9.1): a crash can never leave one without the other. Returns the (possibly pre-existing) row. */
export async function enqueue<K extends CommandKey>(
  db: GkDb, key: K, payload: PayloadOf<K>, ctx: EnqueueContext, opts: EnqueueOptions = {},
): Promise<OutboxRow> {
  // M24 (ADR-125): a command of kind 'system' (forecast.ingest@1) is the server's own; the dispatcher refuses it from every client (COMMAND_SYSTEM_ONLY)
  // and a device never queues one: refused before anything is parsed or written (sync/systemCommands.test.ts)
  if (isSystemCommand(key)) throw new Error('COMMAND_SYSTEM_ONLY')
  const parsed = COMMANDS[key].parse(payload) as Record<string, unknown>   // throws ZodError: the UI validates the same schema
  const [name, versionText] = key.split('@') as [string, string]
  // double-tap guard (ARCHITECTURE 9.14): the same entity + action + payload while the first is still live is a no-op; a command whose values must not stay on the
  // device is keyed by its ids only (sync/transient.ts)
  const intent = opts.intentKey ?? intentKeyOf(key, opts.aggregateId, keyedPayload(name, parsed))

  // the local-only stores too (db.ts LOCAL_ONLY_STORES): a projector may write its device-only row (G's incident stub) in this same transaction
  return db.transaction('rw', [db.outbox, db.overlay, db.meta, db.blobs, ...MIRROR_STORES.map((s) => db.table(s)), ...localOnlyTables(db)], async () => {
    const live = await db.outbox.filter((r) => r.intent_key === intent && LIVE.has(r.status)).first()
    if (live) return live

    const localSeq = ((await getMeta<number>(db, 'localSeq')) ?? 0) + 1
    const knownEpoch = await getMeta<string>(db, 'epoch')
    const row: OutboxRow = {
      command_id: newId(), local_seq: localSeq, name, version: Number(versionText),
      org_id: ctx.orgId, site_id: ctx.siteId, depends_on: opts.dependsOn ?? [], payload: parsed,
      occurred_at: (opts.occurredAt ?? new Date()).toISOString(),
      known_seq: (await getMeta<number>(db, 'knownSeq')) ?? 0,
      ...(knownEpoch ? { known_epoch: knownEpoch } : {}),        // absent until the first pull; the server then uses its own
      clock_offset_ms: (await getMeta<number>(db, 'clockOffsetMs')) ?? 0,
      actor_person_id: ctx.personId,
      status: 'queued', attempts: 0, timeouts: 0, intent_key: intent, auth_mode: ctx.authMode ?? 'personal',
      device_id: ctx.deviceId, app_version: ctx.appVersion, created_at: new Date().toISOString(),
      ...(opts.aggregateId ? { aggregate_id: opts.aggregateId } : {}),
      ...(opts.nonce ? { nonce: opts.nonce } : {}),
      ...(opts.localTag ? { local_tag: opts.localTag } : {}),
      ...(ctx.kioskUnlockId ? { kiosk_unlock_id: ctx.kioskUnlockId } : {}),
    }
    await db.outbox.add(row)
    const projector = PROJECTORS[commandKey(name, row.version) as CommandKey]
    if (projector) await db.overlay.bulkPut(await projector(row, db))
    await setMeta(db, 'localSeq', localSeq)
    if (opts.alsoWrite) await opts.alsoWrite(row)
    return row
  })
}
